import { ResponseStatusEnum } from "#webapp/src/__schema__";
import { mailIcon } from "#webapp/src/assets/img/icons";
import { AvatarPerson } from "#webapp/src/components/avatar/AvatarPerson";
import { EFlex } from "#webapp/src/util/event-tag.util";
import { Link } from "@clockwise/design-system";
import { Person } from "@clockwise/schema";
import classNames from "classnames";
import { isEmpty } from "lodash";
import React, { useMemo, useState } from "react";
import { getAvatarPerson } from "./EventAttendeesAvatars";
import {
  EventSchedulingQueryAttendee,
  EventSchedulingQueryOrg,
  EventSchedulingQueryPerson,
  PersonImpact,
} from "./EventScheduling";
import FocusTimeTooltip, { getName } from "./FocusTimeTooltip";
import { PainPoints } from "./PainPoints";

const AttendeeSlotPains = ({
  org,
  personImpact,
  person,
  attendee,
  hidePainsForAttendeeWhoHaveDeclined,
  onInvite,
  eventFlexStatus,
}: {
  org: EventSchedulingQueryOrg;
  personImpact: PersonImpact;
  person: EventSchedulingQueryPerson;
  attendee: EventSchedulingQueryAttendee | null;
  hidePainsForAttendeeWhoHaveDeclined: boolean;
  onInvite?: (invitee: Person) => void;
  eventFlexStatus: EFlex;
}) => {
  const [isRecentInvitee, setIsRecentInvitee] = useState<boolean>(false);

  const { impacts, calendarId } = personImpact;

  const name = getName(person, personImpact);

  const hasPains =
    impacts?.hasConflict ||
    !isEmpty(impacts?.unavailabilities) ||
    !isEmpty(impacts?.inconveniences);
  const shouldHidePains =
    hidePainsForAttendeeWhoHaveDeclined && attendee?.responseStatus == ResponseStatusEnum.Declined;
  // When there only an avatar and a name, make them center to each other
  const alignCenter = !attendee?.organizer && (!hasPains || shouldHidePains);

  const isNotUser = person && !person.isUser && !person.isYou;
  const userPending =
    person &&
    org.pendingInvites?.edges?.some((edge) => edge?.node?.targetCalendarId === person.primaryEmail);
  const avatarPerson = useMemo(() => getAvatarPerson(person, calendarId), [person, calendarId]);

  const isFlexMeeting = eventFlexStatus !== EFlex.Pin;
  return (
    <div
      key={calendarId}
      className={classNames("cw-flex cw-flex-row cw-align-bottom cw-mb-2 cw-items-start cw-gap-1", {
        "cw-items-center": alignCenter,
      })}
    >
      <AvatarPerson
        person={avatarPerson}
        orgPerson={null}
        toolTipContent={<FocusTimeTooltip impacts={impacts} name={name} />}
        backupCalendarId={calendarId}
        size="medium"
        responseStatus={attendee?.responseStatus}
      />
      <div className={`cw-body-sm cw-ml-2 ${!alignCenter ? "cw-mt-1" : ""}`}>
        <div className="cw-max-w-[212px] cw-text-ellipsis cw-overflow-hidden cw-whitespace-nowrap cw-text-default">
          {name}
        </div>
        {attendee && attendee.organizer && (
          <div className="cw-flex cw-flex-row cw-items-center cw-caption">Organizer</div>
        )}
        {impacts && !shouldHidePains && <PainPoints impacts={impacts} />}
        {attendee && person && !attendee.organizer && onInvite && isNotUser && isFlexMeeting && (
          <div>
            {isRecentInvitee ? (
              <div className="cw-flex cw-items-center cw-pt-1">
                <img className="cw-w-4 cw-mr-[5px]" src={mailIcon} />
                <span className="cw-caption cw-text-positive">Invite Sent!</span>
              </div>
            ) : (
              !userPending && (
                <div className="cw-flex cw-items-center cw-pt-1">
                  <img className="cw-w-4 cw-mr-[5px]" src={mailIcon} />
                  <span className="cw-caption">
                    <Link
                      onClick={() => {
                        onInvite(person);
                        setIsRecentInvitee(true);
                      }}
                    >
                      Invite {person.profile?.givenName} to Clockwise
                    </Link>
                  </span>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendeeSlotPains;
